/**
 * Organization Switcher
 *
 * Dropdown component for switching between organizations.
 * Shows current organization and allows switching when user has multiple orgs.
 */

import { useState, useRef, useEffect } from 'react';
import { useOrganization } from '../../context/OrganizationContext';

interface OrgSwitcherProps {
  variant?: 'sidebar' | 'header';
}

/**
 * Both mount points now sit on graphite chrome — the admin sidebar and the
 * crew header — so the two variants differ in density and truncation, not in
 * palette. They are kept apart because the crew header has to survive a long
 * org name on a phone.
 */

/**
 * Compact initials for narrow screens: "Robert Morris University" -> "RMU".
 * A long org name in the phone header pushed the whole row off-screen
 * (found on a real iPhone, 2026-08-09).
 */
function abbreviate(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return words.slice(0, 4).map((w) => w[0]).join('').toUpperCase();
  }
  return name.slice(0, 4).toUpperCase();
}

export default function OrgSwitcher({ variant = 'sidebar' }: OrgSwitcherProps) {
  const { organizations, currentOrg, switchOrganization, hasMultipleOrgs } =
    useOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if no organization selected
  if (!currentOrg) {
    return null;
  }

  // If only one org, show non-interactive display
  if (!hasMultipleOrgs) {
    return (
      <div className={variant === 'sidebar' ? 'px-3 py-2' : ''}>
        <div
          className={`
            flex items-center
            ${variant === 'sidebar' ? 'text-onDark-body' : 'text-onDark-body'}
          `}
        >
          <OrgIcon className="w-5 h-5 mr-2 flex-shrink-0" />
          {variant === 'header' ? (
            <>
              <span className="text-sm font-medium sm:hidden" title={currentOrg.orgName}>
                {abbreviate(currentOrg.orgName)}
              </span>
              <span className="hidden sm:inline text-sm font-medium truncate max-w-[14rem]">
                {currentOrg.orgName}
              </span>
            </>
          ) : (
            <span className="text-sm font-medium truncate">{currentOrg.orgName}</span>
          )}
        </div>
      </div>
    );
  }

  const handleSwitch = (orgSlug: string) => {
    switchOrganization(orgSlug);
    setIsOpen(false);
    // Reload the page to fetch fresh data for the new org
    window.location.reload();
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center w-full text-left transition-colors
          ${
            variant === 'sidebar'
              ? 'px-3 py-2 text-onDark-body hover:bg-rack-raised hover:text-white'
              : 'px-2 py-1 text-onDark-body hover:bg-rack-raised hover:text-white'
          }
        `}
      >
        <OrgIcon className="w-5 h-5 mr-2 flex-shrink-0" />
        {variant === 'header' ? (
          <>
            <span className="text-sm font-medium sm:hidden" title={currentOrg.orgName}>
              {abbreviate(currentOrg.orgName)}
            </span>
            <span className="hidden sm:inline text-sm font-medium truncate max-w-[14rem]">
              {currentOrg.orgName}
            </span>
          </>
        ) : (
          <span className="text-sm font-medium truncate flex-1">{currentOrg.orgName}</span>
        )}
        <ChevronIcon
          className={`w-4 h-4 ml-2 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className="absolute z-50 mt-1 w-full min-w-[200px] bg-rack-deep border border-rack shadow-float"
        >
          <div className="py-1">
            {organizations.map((org) => (
              <button
                key={org.orgId}
                onClick={() => handleSwitch(org.orgSlug)}
                className={`
                  w-full flex items-center px-3 py-2 text-sm transition-colors
                  ${
                    org.orgSlug === currentOrg.orgSlug
                      ? 'bg-rack-raised text-white'
                      : 'text-onDark-body hover:bg-rack-raised hover:text-white'
                  }
                `}
              >
                <span className="truncate flex-1">{org.orgName}</span>
                {org.orgSlug === currentOrg.orgSlug && (
                  <CheckIcon className="w-4 h-4 ml-2 flex-shrink-0" />
                )}
                <span className="ml-2 text-xs px-1.5 py-0.5 border border-rack-unlit text-onDark-secondary">
                  {org.role}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function OrgIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}
